import React, { createContext, useContext, useState, useEffect } from 'react'
import { Connection, PublicKey, LAMPORTS_PER_SOL } from '@solana/web3.js'
import { SOLANA_RPC_ENDPOINT, BODY_TOKEN_MINT, TOKEN_INFO } from '../config/solana'

const WalletContext = createContext()

export function useWallet() {
  return useContext(WalletContext)
}

export function WalletProvider({ children }) {
  const [walletType, setWalletType] = useState(null) // 'ethereum' or 'solana'
  const [account, setAccount] = useState(null)
  const [balance, setBalance] = useState('0')
  const [tokenBalance, setTokenBalance] = useState('0')
  const [stakedBalance, setStakedBalance] = useState('0')
  const [availableBalance, setAvailableBalance] = useState('0')
  const [network, setNetwork] = useState(null)
  const [isConnecting, setIsConnecting] = useState(false)
  const [error, setError] = useState(null)

  // Check if wallet was previously connected
  useEffect(() => {
    const wasConnected = localStorage.getItem('walletConnected')
    const savedWalletType = localStorage.getItem('walletType')
    
    if (wasConnected === 'true' && savedWalletType) {
      if (savedWalletType === 'solana') {
        connectSolanaWallet()
      }
    }
    
    // Listen for Phantom (Solana) account changes
    if (window.solana && window.solana.isPhantom) {
      window.solana.on('accountChanged', handleSolanaAccountChanged)
    }

    return () => {
      if (window.solana && window.solana.isPhantom) {
        window.solana.removeListener('accountChanged', handleSolanaAccountChanged)
      }
    }
  }, [])

  const handleSolanaAccountChanged = (publicKey) => {
    if (!publicKey) {
      disconnectWallet()
    } else {
      setAccount(publicKey.toString())
      updateSolanaBalances(publicKey)
    }
  }

  const connectSolanaWallet = async () => {
    setIsConnecting(true)
    setError(null)

    try {
      // Check if Phantom is installed
      if (!window.solana || !window.solana.isPhantom) {
        throw new Error('Phantom wallet is not installed. Please install Phantom to continue.')
      }

      // Connect to Phantom
      const response = await window.solana.connect()
      const publicKey = response.publicKey
      
      setAccount(publicKey.toString())
      setWalletType('solana')
      setNetwork({
        name: 'Solana Mainnet',
        chainId: 'mainnet-beta'
      })

      // Get balances
      await updateSolanaBalances(publicKey)

      // Store connection state
      localStorage.setItem('walletConnected', 'true')
      localStorage.setItem('walletType', 'solana')

    } catch (err) {
      console.error('Solana wallet connection error:', err)
      setError(err.message || 'Failed to connect Phantom wallet')
      localStorage.removeItem('walletConnected')
      localStorage.removeItem('walletType')
    } finally {
      setIsConnecting(false)
    }
  }

  const updateSolanaBalances = async (publicKey) => {
    try {
      const connection = new Connection(SOLANA_RPC_ENDPOINT, 'confirmed')
      const walletAddress = publicKey.toString()
      
      // Get real SOL balance from Mainnet
      const solBalance = await connection.getBalance(publicKey)
      setBalance((solBalance / LAMPORTS_PER_SOL).toFixed(4))

      // Get REAL $BODY token balance from blockchain! 🚀
      let totalTokens = 0
      try {
        const TOKEN_PROGRAM_ID = new PublicKey('TokenkegQfeZyiNwAJbNbGKPFXCWuBvf9Ss623VQ5DA')
        const mintPubkey = new PublicKey(BODY_TOKEN_MINT)
        
        const accounts = await connection.getParsedTokenAccountsByOwner(
          publicKey,
          { mint: mintPubkey }
        )

        if (accounts.value.length > 0) {
          const tokenAmount = accounts.value[0].account.data.parsed.info.tokenAmount
          totalTokens = parseFloat(tokenAmount.uiAmount)
          setTokenBalance(totalTokens.toLocaleString('en-US', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
          }))
        } else {
          // Wallet doesn't have BODY tokens yet
          setTokenBalance('0.00')
          totalTokens = 0
        }
      } catch (tokenErr) {
        console.error('Error fetching BODY token balance:', tokenErr)
        setTokenBalance('0.00')
        totalTokens = 0
      }

      // Fetch staked amount from backend
      try {
        const backendUrl = process.env.REACT_APP_BACKEND_URL
        const response = await fetch(`${backendUrl}/api/stakes/stats?wallet_address=${walletAddress}`)
        if (response.ok) {
          const stats = await response.json()
          const staked = stats.user_total_staked || 0
          const penalties = stats.user_total_penalties || 0
          
          setStakedBalance(staked.toLocaleString('en-US', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
          }))
          
          // Calculate available balance
          // Available = Total - Staked - Penalties (penalties are "burned"/lost)
          const available = Math.max(0, totalTokens - staked - penalties)
          setAvailableBalance(available.toLocaleString('en-US', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
          }))
        } else {
          setStakedBalance('0.00')
          setAvailableBalance(totalTokens.toLocaleString('en-US', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
          }))
        }
      } catch (stakingErr) {
        console.error('Error fetching staking balance:', stakingErr)
        setStakedBalance('0.00')
        setAvailableBalance(totalTokens.toLocaleString('en-US', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        }))
      }

    } catch (err) {
      console.error('Solana balance update error:', err)
      // Fallback
      setBalance('0.0000')
      setTokenBalance('0.00')
      setStakedBalance('0.00')
      setAvailableBalance('0.00')
    }
  }

  const disconnectWallet = () => {
    if (walletType === 'solana' && window.solana) {
      window.solana.disconnect()
    }
    setAccount(null)
    setBalance('0')
    setTokenBalance('0')
    setStakedBalance('0')
    setAvailableBalance('0')
    setNetwork(null)
    setError(null)
    setWalletType(null)
    localStorage.removeItem('walletConnected')
    localStorage.removeItem('walletType')
  }

  const refreshBalances = async () => {
    if (account && window.solana) {
      const publicKey = await window.solana.connect()
      await updateSolanaBalances(publicKey.publicKey)
    }
  }

  const formatAddress = (address) => {
    if (!address) return ''
    return `${address.substring(0, 4)}...${address.substring(address.length - 4)}`
  }

  const value = {
    walletType,
    account,
    balance,
    tokenBalance,
    stakedBalance,
    availableBalance,
    network,
    isConnecting,
    error,
    connectWallet: connectSolanaWallet,
    disconnectWallet,
    refreshBalances,
    formatAddress,
    isConnected: !!account,
    tokenSymbol: TOKEN_INFO.symbol,
    tokenName: TOKEN_INFO.name
  }

  return (
    <WalletContext.Provider value={value}>
      {children}
    </WalletContext.Provider>
  )
}
